import Vue from 'vue'
import Router, { Route, Location } from 'vue-router'

import PaymentModal from '@/components/modals/PaymentModal.vue'

import { isIslandHopping, humanizedCompanyName } from '@/globalMixin'
import store from '@/store'
import { CustomerActionTypes } from '@/store/modules/customer'
import { CustomerNotificationsActionTypes } from '@/store/modules/customerNotifications'
import Booking from '@/views/Booking.vue'
import BookingMessagesPage from '@/views/BookingMessagesPage.vue'
import CreditNotesPage from '@/views/CreditNotesPage.vue'
import Dashboard from '@/views/Dashboard.vue'
import ForgotPassword from '@/views/ForgotPassword.vue'
import InvitePage from '@/views/InvitePage.vue'
import AuthenticatedLayout from '@/views/layouts/AuthenticatedLayout.vue'
import ErrorLayout from '@/views/layouts/ErrorLayout.vue'
import UnauthenticatedLayout from '@/views/layouts/UnauthenticatedLayout.vue'
import WelcomeLayout from '@/views/layouts/WelcomeLayout.vue'
import Login from '@/views/Login.vue'
import NewPasswordPage from '@/views/NewPasswordPage.vue'
import NotificationsPage from '@/views/NotificationsPage.vue'
import PageNotFound from '@/views/PageNotFound.vue'
import Payments from '@/views/Payments.vue'
import Profile from '@/views/Profile.vue'
import Registration from '@/views/Registration.vue'
import ResetPassword from '@/views/ResetPassword.vue'
import ReviewPage from '@/views/ReviewPage.vue'

import { UtilityActionTypes } from './store/modules/utility'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '',
      component: AuthenticatedLayout,
      beforeEnter: (to: Route, from: Route, next: (to?: Location) => void) => {
        store.dispatch(CustomerActionTypes.GetCurrent).then(() => {
          if (to.query.refresh === 'true') {
            store.dispatch(UtilityActionTypes.RefreshAllPages)
          }
          if (!store.getters.isLogined) {
            next({ name: 'login', query: { redirect: to.fullPath } })
          } else if (store.getters.isNewAccount && !to.query.staff) {
            next({ name: 'change_password' })
          } else if (store.getters.passwordResetRequired && !to.query.staff) {
            next({ name: 'change_password' })
          } else {
            store.dispatch(CustomerNotificationsActionTypes.HeaderLoad)
            next()
          }
        })
      },
      children: [
        {
          path: '/',
          name: 'dashboard',
          meta: {
            title: (): string => `Dashboard | ${humanizedCompanyName()}™`,
          },
          component: Dashboard,
        },
        {
          path: 'bookings/:id(\\d+)',
          name: 'booking',
          meta: {
            title: (): string =>
              `Your ${
                isIslandHopping() ? 'Tour' : 'Cruise'
              } | ${humanizedCompanyName()}™`,
          },
          component: Booking,
          children: [
            {
              path: 'new_payment',
              name: 'newPayment',
              component: PaymentModal,
              meta: {
                title: (): string =>
                  `Make a Payment | ${humanizedCompanyName()}™`,
              },
            },
          ],
        },
        {
          path: 'bookings/:id(\\d+)/messages',
          name: 'booking_messages',
          meta: {
            title: (route: Route): string =>
              `Messages - #${route.params.id} | ${humanizedCompanyName()}™`,
          },
          component: BookingMessagesPage,
        },
        {
          path: 'bookings/:id(\\d+)/payments',
          name: 'payments',
          meta: {
            title: (route: Route): string =>
              `Payments - #${route.params.id} | ${humanizedCompanyName()}™`,
          },
          component: Payments,
        },
        {
          path: 'bookings/:id(\\d+)/credit_notes',
          name: 'credit_notes',
          meta: {
            title: (): string => `Credit Notes | ${humanizedCompanyName()}™`,
          },
          component: CreditNotesPage,
        },
        {
          path: 'bookings/:id(\\d+)/review',
          name: 'review',
          meta: {
            title: (route: Route): string =>
              `Review - #${route.params.id} | ${humanizedCompanyName()}™`,
          },
          component: ReviewPage,
        },
        {
          path: 'notifiations',
          name: 'notifications',
          meta: {
            title: (): string => `Notifications | ${humanizedCompanyName()}™`,
          },
          component: NotificationsPage,
        },
        {
          path: '/profile',
          name: 'profile',
          meta: {
            title: (): string => `Profile | ${humanizedCompanyName()}™`,
            hideFooter: true,
          },
          component: Profile,
        },
      ],
    },
    {
      path: '',
      component: UnauthenticatedLayout,
      beforeEnter: (to: Route, from: Route, next: (to?: Location) => void) => {
        store.dispatch(CustomerActionTypes.GetCurrent).then(() => {
          if (store.getters.isLogined) {
            next({ name: 'dashboard' })
          } else {
            next()
          }
        })
      },
      children: [
        {
          path: '/login',
          name: 'login',
          meta: { title: (): string => `Login | ${humanizedCompanyName()}™` },
          component: Login,
        },
        {
          path: '/registration',
          name: 'registration',
          meta: {
            title: (): string => `Create Account  | ${humanizedCompanyName()}™`,
          },
          component: Registration,
        },
        {
          path: '/password/new',
          name: 'forgot_password',
          meta: {
            title: (): string => `Forgot Password | ${humanizedCompanyName()}™`,
          },
          component: ForgotPassword,
        },
        {
          path: '/password/edit',
          name: 'reset_password',
          meta: {
            title: (): string => `Reset Password | ${humanizedCompanyName()}™`,
          },
          component: ResetPassword,
        },
      ],
    },
    {
      path: '/invite',
      name: 'invite',
      meta: { title: (): string => `Invitation | ${humanizedCompanyName()}™` },
      component: UnauthenticatedLayout,
      beforeEnter: (to: Route, from: Route, next: (to?: Location) => void) => {
        store.dispatch(CustomerActionTypes.GetCurrent).then(() => {
          next()
        })
      },
      children: [
        {
          path: ':autologinToken',
          name: 'invitePage',
          meta: { title: (): string => `Invite | ${humanizedCompanyName()}™` },
          component: InvitePage,
        },
      ],
    },
    {
      path: '/welcome',
      component: WelcomeLayout,
      name: 'welcome',
      meta: { title: (): string => `Welcome | ${humanizedCompanyName()}™` },
      beforeEnter: (to: Route, from: Route, next: (to?: Location) => void) => {
        store.dispatch(CustomerActionTypes.GetCurrent).then(() => {
          if (!store.getters.isLogined) {
            next({ name: 'login', query: { redirect: to.fullPath } })
          }

          next()
        })
      },
      children: [
        {
          path: 'change_password',
          name: 'change_password',
          meta: {
            title: (): string => `Password Change | ${humanizedCompanyName()}™`,
          },
          component: NewPasswordPage,
        },
      ],
    },
    {
      // Autologin links for customers, currently implemented this way to address legacy links and domain names swapping
      // I suggest refactoring it into API calls instead of redirects once old links get expired
      path: '/customers/auto',
      name: 'auto_login',
      meta: { title: (): string => `Login | ${humanizedCompanyName()}™` },
      beforeEnter: (to: Route) => {
        window.location.href = `${process.env.VUE_APP_STATIC_ROOT}${to.fullPath}`
      },
    },
    // TODO: can be removed later on when all the old review links are expired
    {
      path: '/reviews/new',
      name: 'reviews_redirect',
      meta: { title: (): string => `Review | ${humanizedCompanyName()}™` },
      beforeEnter: (to: Route) => {
        window.location.href = `${process.env.VUE_APP_STATIC_ROOT}${to.fullPath}`
      },
    },
    {
      path: '',
      name: '404',
      component: ErrorLayout,
      beforeEnter: (to: Route, from: Route, next: (to?: Location) => void) => {
        store
          .dispatch(CustomerActionTypes.GetCurrent)
          .then(() => {
            store.dispatch(CustomerNotificationsActionTypes.HeaderLoad)
          })
          .then(() => {
            next()
          })
      },
      children: [
        {
          path: '/404',
          name: 'not_found_page',
          component: PageNotFound,
          meta: {
            title: (): string => `404 - Not Found | ${humanizedCompanyName()}™`,
          },
        },
      ],
    },
    {
      path: '*',
      redirect: '/404',
    },
  ],
})

router.afterEach((to) => {
  Vue.nextTick(() => {
    document.title = to.meta.title(to)
  })
})

export default router
